import React from 'react'

interface StatusInfo {
    label: string
    color: string
}

interface Props {
    value: string | null | undefined
    map: Record<string, StatusInfo>
    /** Antd-ish size; controls font-size and padding. */
    size?: 'small' | 'middle'
    /** Optional fallback for unknown values. Defaults to `value || '—'`. */
    fallback?: React.ReactNode
}

const hexToRgb = (hex: string): [number, number, number] => {
    const clean = hex.replace('#', '')
    return [
        parseInt(clean.slice(0, 2), 16),
        parseInt(clean.slice(2, 4), 16),
        parseInt(clean.slice(4, 6), 16),
    ]
}

/**
 * Unified status badge — replaces inline render functions in RequestResource,
 * vehicleNeedsUtils.renderTaskStatus/renderExecutorStatus, and TaskDelaysCell.statusBadge.
 *
 * Accepts only hex (`#RRGGBB`) color codes — antd colour names (e.g. `green`)
 * fall through to the basic rendering without RGBA tinting.
 */
export const StatusBadge: React.FC<Props> = ({ value, map, size = 'middle', fallback }) => {
    if (!value) return <>{fallback ?? '—'}</>
    const info = map[value]
    if (!info) return <>{fallback ?? value}</>

    const fontSize = size === 'small' ? 12 : 13
    const fontWeight = size === 'small' ? 500 : 400
    const padding = size === 'small' ? '2px 8px' : '2px 8px'

    if (!info.color.startsWith('#')) {
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                padding, borderRadius: '4px', fontSize, fontWeight,
            }}>
                {info.label}
            </span>
        )
    }

    const [r, g, b] = hexToRgb(info.color)
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: `rgba(${r},${g},${b},0.85)`,
            color: '#ffffff',
            border: `1px solid rgba(${r},${g},${b},1)`,
            borderRadius: '4px', padding, fontSize, fontWeight,
        }}>
            {info.label}
        </span>
    )
}
